/**
 * The sign-in gate — LV §133, Arch §16.
 *
 * Microsoft Entra ID via OIDC is the target, and the production flow is a redirect the
 * *browser* performs — no password ever reaches this application, and none is stored
 * (LV §18). Until the tenant is configured (see the open-questions log), the
 * development mode accepts a token minted by
 * `python -m app.cli issue-dev-token`, which is the same shape the real flow produces so
 * nothing downstream behaves differently.
 *
 * `AUTH_MODE=DEV` cannot be enabled in STAGING or PROD — `get_settings()` refuses to
 * start — so this screen's development path is unreachable in a deployed environment.
 *
 * **This page is served to the public, and it says as little as it can.** It is reachable
 * unauthenticated at the application's own hostname, so everything on it is readable by
 * anyone who finds the address. An earlier version of this screen filled its empty half
 * with a four-entry summary of what the system manages — the asset and criticality model,
 * the DIN/VDI inspection regime, that documents are versioned and audit-proof. That is a
 * reconnaissance map for anyone preparing a targeted or social-engineering attempt, and
 * it told a legitimate technician nothing they do not already know from using the system
 * every shift. It is gone, and so is the one-line description above it (LV §141, §158).
 *
 * What stays is what a user needs in order to know they are in the right place and to
 * act: the product name, the operator's name — both already public, in `index.html`'s
 * `<title>` and `meta description` — the sign-in action, which server this is, and what
 * to do when an account does not work.
 *
 * There is deliberately **no** background image either: this is an offline-first PWA
 * whose precache already justifies restricting the typeface to the `latin-*` subsets,
 * every contrast pair in `tests/unit/contrast.test.ts` is measured against a *token*
 * rather than a photograph, and it is the screen every user sees at the start of every
 * shift, where ornament becomes latency and then irritation.
 *
 * All geometry and colour live in `styles/base.css` under `.login*` (LV §6, §124).
 * Nothing here carries an inline style.
 */

import { type ReactNode, useCallback, useEffect, useId, useState } from "react";

import dahDrop from "@/assets/dah-tropfen.png";
import { Alert, Button, Field, TextInput } from "@/components";
import { hasSession, setToken, subscribe } from "@/auth/session";
import { fetchAuthConfig, startLogin, takeLoginError } from "@/auth/oidc";
import type { AuthConfig } from "@/auth/oidc";
import { ApiError } from "@/services/http";

/**
 * Not every refusal is an error the user made.
 *
 * `USER_NOT_PROVISIONED`, the two identity-binding codes and the three consent codes all
 * mean "your credentials are fine, somebody else has to act" — painting those in
 * `--danger` tells a new starter on their first morning that something is broken when
 * nothing is. `danger` is kept for the cases where access really is gone or the attempt
 * really did fail.
 */
function refusalTone(code: string): "warning" | "danger" {
  return [
    "USER_NOT_PROVISIONED",
    "USER_IDENTITY_MISMATCH",
    "USER_EMAIL_AMBIGUOUS",
  ].includes(code) ||
    ["consent_required", "interaction_required", "login_required"].includes(code)
    ? "warning"
    : "danger";
}

/**
 * The provider's refusal code, turned into a sentence that names the user's next action.
 *
 * The old copy interpolated the raw code into "Die Anmeldung wurde abgebrochen (…)",
 * which is simply false for three of the codes it will most often carry: with
 * `consent_required` nobody cancelled anything, an administrator has to approve the
 * application once. docs/10 item 7 and decision 075 record that the first-time consent
 * dialog has never been observed against the real tenant, so this is both the untested
 * path and the one most likely to fire on the first real rollout — the wrong sentence
 * would send the whole pilot group looking for a cancel button they never pressed.
 *
 * The raw code still reaches the user, in `Alert`'s `code` slot, because that is the
 * string support will search for. It is not put inside the German sentence.
 */
function refusalMessage(code: string): string {
  // The two the *application* refuses, as opposed to the provider. They arrive as the
  // backend's own error codes, upper case, because that is the string the log line and
  // this screen have to agree on — see `_refused` in `backend/app/api/v1/auth.py`.
  //
  // `USER_NOT_PROVISIONED` is the one every new employee meets, and until now it was not
  // a message at all: it rendered as a raw JSON envelope at an API URL. The sentence has
  // to do two things — say that the Microsoft half worked, so the user does not go
  // hunting for a password problem that does not exist, and name the one action that
  // gets them in (LV §18).
  if (code === "USER_NOT_PROVISIONED") {
    return (
      "Ihre Anmeldung bei Microsoft war erfolgreich, aber für dieses Konto ist in der " +
      "Instandhaltungssoftware noch kein Benutzer angelegt. Benutzerkonten legt die " +
      "Administration an; eine Selbstregistrierung gibt es nicht. Bitten Sie die " +
      "Systemadministration, Sie anzulegen, und nennen Sie dabei Ihre DAH-E-Mail-Adresse."
    );
  }
  if (code === "USER_INACTIVE") {
    return (
      "Dieses Benutzerkonto ist deaktiviert. Der Zugang wurde entzogen — wenden Sie " +
      "sich an die Systemadministration, wenn Sie ihn wieder benötigen."
    );
  }
  // The two refusals that come from the identity *binding* rather than from the account
  // existing. Both need an administrator at the user record, and neither is anything the
  // user can retry — so the sentence says so instead of inviting another attempt.
  if (code === "USER_IDENTITY_MISMATCH") {
    return (
      "Zu Ihrer E-Mail-Adresse gibt es ein Benutzerkonto, das aber mit einer anderen " +
      "Microsoft-Anmeldung verknüpft ist. Das kann passieren, wenn ein Konto neu " +
      "angelegt wurde. Die Systemadministration muss die Verknüpfung korrigieren; ein " +
      "erneuter Anmeldeversuch ändert daran nichts."
    );
  }
  if (code === "USER_EMAIL_AMBIGUOUS") {
    return (
      "Ihre E-Mail-Adresse ist mehr als einem Benutzerkonto zugeordnet. Solange das so " +
      "ist, kann die Anmeldung nicht eindeutig zugeordnet werden — die " +
      "Systemadministration muss die doppelte Anlage auflösen."
    );
  }

  // `consent_required` and its two siblings all mean the same thing to the user: an
  // administrator has to approve the application once.
  if (["consent_required", "interaction_required", "login_required"].includes(code)) {
    return (
      "Für den Zugriff auf diese Anwendung fehlt noch die Freigabe durch Ihre " +
      "Organisation. Ihre Anmeldedaten sind in Ordnung — die Freigabe muss einmalig " +
      "erteilt werden. Melden Sie den unten angezeigten Fehlercode der " +
      "Systemadministration."
    );
  }
  if (code === "access_denied") {
    return (
      "Die Anmeldung bei Microsoft wurde abgebrochen. Sie haben den Vorgang beendet " +
      "oder die Zustimmung nicht erteilt. Tippen Sie erneut auf „Bei Microsoft " +
      "anmelden“."
    );
  }
  return (
    "Die Anmeldung bei Microsoft ist fehlgeschlagen. Versuchen Sie es bitte erneut. " +
    "Bleibt der Fehler, melden Sie ihn mit dem angezeigten Fehlercode und der Uhrzeit " +
    "der Systemadministration."
  );
}

export function Login({
  children,
  onAuthenticated,
}: {
  children: ReactNode;
  onAuthenticated: () => Promise<boolean>;
}) {
  const [authenticated, setAuthenticated] = useState(false);
  const [checking, setChecking] = useState(true);
  const [token, setTokenInput] = useState("");
  const [error, setError] = useState<{
    message: string;
    code?: string;
    tone: "warning" | "danger";
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [config, setConfig] = useState<AuthConfig | null>(null);
  const [configUnreachable, setConfigUnreachable] = useState(false);
  const panelHeadingId = useId();

  /**
   * Ask the server what it offers before drawing either option. The button used to be
   * rendered unconditionally and led to a route that did not exist.
   *
   * **Retried, and re-asked when the network returns.** This used to be one `fetch` in
   * a `useEffect(..., [])`, which asks exactly once for the life of the component — and
   * the answer gates the development token form, which on a DEV server is the *only* way
   * in. So a single failed call removed the way in permanently, with no path back even
   * once the server was reachable again.
   *
   * That is not hypothetical: it took out the E2E suite in CI run 20260910.3.
   * `offline-sync.spec.ts` signs in, goes offline, comes back online and signs in again;
   * the mount that happened while offline could never re-ask, so the disclosure the
   * helper clicks was simply not in the DOM and the test sat on it for 240 seconds.
   *
   * It is the right shape for this application beyond the test, too. This is an
   * offline-first PWA (LV §127-132): the service worker serves this shell with no
   * connection at all, while "what does this server offer" can only be answered online.
   */
  const askConfig = useCallback(async (): Promise<void> => {
    setConfigUnreachable(false);
    // Three tries over ~2.4s. Enough for a cold API or a blip, short enough that a
    // genuinely unreachable server is reported rather than waited on.
    for (const delay of [0, 600, 1800]) {
      if (delay) await new Promise((resolve) => setTimeout(resolve, delay));
      const answer = await fetchAuthConfig();
      if (answer) {
        setConfig(answer);
        return;
      }
    }
    setConfigUnreachable(true);
  }, []);

  useEffect(() => {
    void askConfig();
  }, [askConfig]);

  // The moment the browser says it has a network again, ask again.
  useEffect(() => {
    const again = (): void => {
      void askConfig();
    };
    window.addEventListener("online", again);
    return () => window.removeEventListener("online", again);
  }, [askConfig]);

  // The session lives in a module variable, not in web storage (Arch §16), so this
  // subscribes to it rather than reading storage on each render. `hasSession`, not
  // "is there a sendable token": keyed on the token, this screen took over every time
  // one expired, even with its renewal a moment away (#411).
  useEffect(() => subscribe(() => setAuthenticated(hasSession())), []);

  useEffect(() => {
    void (async () => {
      setAuthenticated(await onAuthenticated());
      // The backend puts a provider refusal in the fragment; `bootstrapSession`
      // has already consumed and cleared it by now.
      const refused = takeLoginError();
      if (refused) {
        setError({
          message: refusalMessage(refused),
          code: refused,
          tone: refusalTone(refused),
        });
      }
      setChecking(false);
    })();
  }, [onAuthenticated]);

  if (authenticated) return <>{children}</>;

  const signIn = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      setToken(token.trim());
      const ok = await onAuthenticated();
      if (!ok) {
        // Three server answers reach this one branch — a malformed or expired token,
        // `USER_NOT_PROVISIONED` and `USER_INACTIVE` — because `onAuthenticated`
        // returns a boolean and throws the code away. Telling them apart needs that
        // signature widened, which is a change to `bootstrapSession` and its callers;
        // until then the sentence has to cover all three without guessing which it was.
        // What it must not do is quote a contract clause at a technician: the old copy
        // ended "(LV §18)", a reference into a 92-page procurement document no user of
        // this system will ever hold. The rule belongs in this comment (LV §18, §126).
        setError({
          tone: "danger",
          message:
            "Das Token wurde nicht akzeptiert. Entweder ist es ungültig oder " +
            "abgelaufen — erzeugen Sie in diesem Fall ein neues und fügen Sie es " +
            "vollständig ein — oder für dieses Konto ist noch kein Benutzer angelegt. " +
            "Benutzerkonten legt die Administration an; eine Selbstregistrierung gibt " +
            "es nicht.",
        });
      }
      setAuthenticated(ok);
    } catch (cause) {
      setError({
        tone: "danger",
        message:
          cause instanceof ApiError
            ? cause.message
            : "Die Anmeldung konnte nicht durchgeführt werden — der Server ist nicht " +
              "erreichbar. Prüfen Sie Ihre Netzwerkverbindung und versuchen Sie es " +
              "erneut. Bleibt die Meldung, melden Sie sie der Systemadministration.",
        code: cause instanceof ApiError ? cause.code : undefined,
      });
    } finally {
      setBusy(false);
    }
  };

  const interactive = config === null || config.interactive_login_available;

  return (
    <div className="login">
      {/* A landmark, so the page's content is inside one. The app's own `<main>` and
          skip link live in the Shell, which does not render until after sign-in — so
          before sign-in 100 % of the content sat outside any region. No skip link
          here: there is no repeated block to bypass, so WCAG 2.4.1 is not engaged. */}
      <main
        className="login__layout"
        id="main-content"
        aria-busy={checking || undefined}
      >
        {/* Identity, and nothing beyond it. See the note above `.login` in `base.css`
            for what this block deliberately does *not* say. */}
        <div className="login__identity">
          {/* The DAH Gruppe drop, as in the sidebar. Public anyway — it is the
              operator's own mark — and decorative: the h1 names the product. */}
          <img className="login__mark" src={dahDrop} alt="" />

          {/* The E2E suite's first assertion, and the page's only h1. */}
          <h1 className="login__title">DAH Maintenance Hub</h1>
          <p className="login__subtitle">
            Instandhaltungsmanagement der DAH Energie GmbH
          </p>
        </div>

        <div className="login__action">
          <EnvironmentNotice config={config} />

          <section className="login__panel" aria-labelledby={panelHeadingId}>
            <h2 className="login__panel-title" id={panelHeadingId}>
              Anmeldung
            </h2>

            {/* One live region, mounted for the whole life of the page rather than
                swapped in with its text already in place — a region that arrives
                populated is announced unreliably or not at all. It is visible while
                the check runs and goes off-screen afterwards, so the *transition* to
                a usable page is spoken (WCAG 4.1.3).

                It also carries the refusal text, and that is why it has to: a refusal
                arriving back from the provider lands in an `Alert` that mounts *with*
                its message, and the two most important ones — "no account yet" and
                "consent needed" — are `warning` tone, so the `Alert` is `role="status"`
                and may say nothing at all. Announcing here is duplicated for the
                `danger` tones, which is a small cost against a silent failure. */}
            <p className={checking ? "login__status" : "visually-hidden"} role="status">
              {checking
                ? "Anmeldung wird geprüft…"
                : (error?.message ?? "Anmeldung erforderlich.")}
            </p>

            {checking ? null : (
              <>
                {interactive ? (
                  <>
                    <Button
                      variant="primary"
                      className="login__signin"
                      disabled={redirecting}
                      onClick={() => {
                        // `startLogin` assigns `window.location.href` and returns;
                        // there is no completion event to await, because the document
                        // is about to be replaced. The announcement at the moment of
                        // the click is therefore the only feedback that can exist —
                        // without it the page sat looking untouched for the seconds it
                        // takes to reach the tenant and users tapped again, restarting
                        // the navigation each time.
                        setRedirecting(true);
                        startLogin();
                      }}
                    >
                      <MicrosoftMark />
                      {redirecting
                        ? "Weiterleitung zu Microsoft…"
                        : "Bei Microsoft anmelden"}
                    </Button>
                    <p className="login__hint">
                      Melden Sie sich mit Ihrem Geschäfts-, Schul- oder Unikonto der DAH
                      Energie GmbH an — dem Konto, mit dem Sie auch Outlook und Teams
                      nutzen. Ihr Kennwort geben Sie bei Microsoft ein; diese Anwendung
                      sieht es nicht und speichert es nicht.
                    </p>
                    <p className="login__hint">
                      Noch kein Zugang? Benutzerkonten legt die Administration an; eine
                      Selbstregistrierung gibt es nicht. Lassen Sie den Zugang von Ihrer
                      Führungskraft beantragen.
                    </p>
                  </>
                ) : (
                  <Alert
                    tone="info"
                    title="Anmeldung über Microsoft ist hier nicht eingerichtet"
                  >
                    Dieser Server ist nicht mit dem Anmeldedienst der DAH Energie GmbH
                    verbunden. Die Anmeldung erfolgt bis dahin über einen
                    Entwicklungszugang. Wenn Sie sich hier anmelden sollten, wenden Sie
                    sich an die Systemadministration.
                  </Alert>
                )}

                {/* Said out loud rather than guessed at. With no answer from
                    `/auth/config` the page cannot know what this server offers, and the
                    development token form stays hidden (see `DevTokenSection` for why
                    that half fails closed). A user looking at a screen with no way in
                    deserves to be told that, and to be able to try again. */}
                {configUnreachable ? (
                  <Alert
                    tone="danger"
                    title="Anmeldedienst nicht erreichbar"
                    code="AUTH_CONFIG_UNREACHABLE"
                  >
                    Der Server hat nicht geantwortet. Pruefen Sie Ihre
                    Netzwerkverbindung und versuchen Sie es erneut. Bleibt die Meldung,
                    ist die Anwendung derzeit nicht verfuegbar — wenden Sie sich an die
                    Systemadministration.
                    <div className="login__retry">
                      <Button
                        onClick={() => {
                          void askConfig();
                        }}
                      >
                        Erneut versuchen
                      </Button>
                    </div>
                  </Alert>
                ) : null}

                {error ? (
                  <Alert tone={error.tone} code={error.code}>
                    {error.message}
                  </Alert>
                ) : null}
              </>
            )}
          </section>

          {checking ? null : (
            <DevTokenSection
              token={token}
              onTokenChange={setTokenInput}
              onSubmit={signIn}
              busy={busy}
              available={config === null ? null : config.dev_token_available}
            />
          )}
        </div>

        {/* Its own area, like the scope list, so it can be last on a phone and sit
            under the sign-in panel on a desktop. Inside `.login__action` it landed
            between the panel and the scope list at 390px. */}
        <LoginFooter config={config} />
      </main>
    </div>
  );
}

/**
 * The Microsoft logo, unmodified.
 *
 * Four squares, Microsoft's own colours, and they are deliberately **not** design-system
 * tokens: Microsoft's branding guidance says in as many words „Verändern Sie das
 * Microsoft-Logo nicht", so the fills cannot be carried into a palette that follows the
 * theme. The same guidance is why the label reads „Bei Microsoft anmelden" and no longer
 * „Mit Microsoft-Konto anmelden" — a *Microsoft-Konto* is the personal consumer account,
 * which no DAH technician has, and the guidance's own DON'T list rules out the
 * alternatives („Unternehmenskonto", „Geschäftskonto", „Firmenkonto"). Its DO is to name
 * „Geschäfts-, Schul- oder Unikonto" beside the button, which the hint below it does.
 *
 * WCAG 2.1 excludes logotypes from 1.4.11 explicitly, which is why these four values sit
 * outside the enumerated list in `tests/unit/contrast.test.ts`. The exemption is also why
 * nothing would have caught the mark going nearly invisible on the dark-mode button —
 * the green square measures 1.04:1 against it — so `.login__ms-mark` pins the logo to a
 * white ground. The numbers are in the comment on that class.
 *
 * Source: learn.microsoft.com/de-de/entra/identity-platform/howto-add-branding-in-apps.
 */
function MicrosoftMark() {
  return (
    <svg
      className="login__ms-mark"
      viewBox="0 0 21 21"
      width="18"
      height="18"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

/**
 * "You are not on the productive system."
 *
 * Shown for every environment except PROD, and on PROD nothing is shown at all — a
 * banner that says every morning that everything is normal is noise. The environment is
 * the server's own code (`LOCAL`, `STAGING`, …) rendered verbatim, because that
 * identifier is what appears in the URL and in a support ticket; inventing German for it
 * would be a guess presented as a label.
 */
function EnvironmentNotice({ config }: { config: AuthConfig | null }) {
  // Positive test, not `!== "PROD"`. An api that predates the field omits it, and
  // `undefined !== "PROD"` would put a "Testumgebung undefined" banner on production —
  // see the note on `AuthConfig.environment`. No value means say nothing.
  const environment = config?.environment;
  if (!environment || environment === "PROD") return null;
  return (
    <Alert
      tone="warning"
      title={`Testumgebung ${environment} — nicht das Produktivsystem`}
    >
      Diese Umgebung dient der Entwicklung und dem Test. Eingaben hier sind nicht
      verbindlich und werden regelmäßig zurückgesetzt.
    </Alert>
  );
}

/**
 * Which server, and which version.
 *
 * Both come from `GET /auth/config`, never from a build-time constant: the same bundle
 * is served against LOCAL, STAGING and PROD, so a compiled-in value would name the wrong
 * server in exactly the case where saying so matters (LV §134, §181). Nothing is rendered
 * until the server has answered — a footer that guesses is worse than no footer.
 *
 * **No theme or contrast control here**, though an earlier version had both. LV §193 asks
 * for an increased-contrast option and the argument for putting one on this screen was
 * that the shell's copies sit behind the gate — but the option is not in fact missing
 * here. `applyDisplayPreferences()` runs before the first render (see `main.tsx`), so a
 * preference set once inside the application applies to this screen from then on; and
 * with nothing stored, `tokens.css` follows the operating system's `prefers-contrast` and
 * `prefers-color-scheme` by default. What two buttons would add is an override for
 * somebody who wants a setting *different* from their OS and has never signed in — at
 * the cost of two controls and two tab stops on the page that should have exactly one
 * thing on it. The in-app control stays the place to change it.
 */
function LoginFooter({ config }: { config: AuthConfig | null }) {
  // Nothing at all on PROD, and nothing before the server has answered. On a test
  // environment the version and the environment code earn their place: the pilot needs
  // to know which build it is looking at, and a support call starts with that string. On
  // the productive system neither helps a user who is about to press one button, and
  // both are readable by anyone who finds the address — a version number on a public
  // sign-in page is free reconnaissance (LV §141, §158). The signed-in application still
  // reports it, to people who have a right to ask.
  // Both values or neither, and nothing on PROD. An older api omits them, and a footer
  // reading "Version undefined - Umgebung undefined" is worse than no footer.
  if (!config?.environment || !config.release_version) return null;
  if (config.environment === "PROD") return null;
  return (
    <footer className="login__footer">
      <p className="login__footer-facts">
        DAH Maintenance Hub · Version {config.release_version} · Umgebung{" "}
        {config.environment}
      </p>
    </footer>
  );
}

/**
 * The development token form.
 *
 * Rendered only when the backend reports it is running in a non-production
 * configuration. Asking the server rather than checking a build-time flag means a
 * production build pointed at a production API never shows it, whatever it was compiled
 * with.
 *
 * It sits in its own subordinate block below the sign-in panel rather than inside it, so
 * the production path is the only thing in the primary panel.
 */
function DevTokenSection({
  token,
  onTokenChange,
  onSubmit,
  busy,
  available,
}: {
  token: string;
  onTokenChange: (value: string) => void;
  onSubmit: () => void | Promise<void>;
  busy: boolean;
  /** From `GET /auth/config`; `null` when the server could not be asked. */
  available: boolean | null;
}) {
  // The server says whether it will accept a development token, rather than this
  // component inferring it from whether an unrelated endpoint returned 401. The old
  // probe could not tell "AUTH_MODE=ENTRA, so no dev tokens" from "not signed in yet",
  // and so offered the form in production too.
  //
  // **Fail closed**, and note that this is the opposite of the decision taken for the
  // interactive button above. `null` means the server could not be reached, and for the
  // Microsoft button keeping it is right — the server answers for itself when the
  // redirect is followed, and hiding the only way in because the network blipped is the
  // worse failure. For *this* form the same reasoning does not hold: `AUTH_MODE=DEV` is
  // refused at startup in STAGING and PROD (`core/config.py`), so there is no deployed
  // state in which it can succeed. Offering it on an unreachable server would present a
  // control guaranteed to fail and disclose that a token path exists (LV §134, §141).
  if (available !== true) return null;

  return (
    <details className="login__dev">
      <summary className="login__dev-summary">
        Entwicklungszugang (nur LOCAL / DEV)
      </summary>
      <div className="login__dev-body">
        <Alert tone="warning" title="Nur für Entwicklung und Test">
          Dieser Zugang existiert ausschließlich auf Entwicklungs- und Testservern; auf
          dem Produktivsystem ist er abgeschaltet. Ein Zugangstoken, das Ihnen jemand
          zusendet, ist immer ein Betrugsversuch — geben Sie es nicht ein und melden Sie
          es der Systemadministration.
        </Alert>
        <form
          className="login__form"
          onSubmit={(event) => {
            event.preventDefault();
            void onSubmit();
          }}
        >
          <Field
            label="Zugangstoken"
            hint={
              <>
                Erzeugen mit:{" "}
                <span className="mono">
                  python -m app.cli issue-dev-token --email …
                </span>
              </>
            }
          >
            {(id) => (
              <TextInput
                id={id}
                value={token}
                onChange={(event) => onTokenChange(event.target.value)}
                placeholder="eyJhbGciOi…"
                autoComplete="off"
                spellCheck={false}
                // A JWT is case-sensitive and base64url. Without these a tablet or
                // phone keyboard capitalises the first character and the token is
                // rejected for a reason the user cannot see; `spellCheck` does not
                // affect capitalisation. `inputMode` is deliberately left alone — the
                // default text keyboard is the right one for a base64url string.
                autoCapitalize="none"
                autoCorrect="off"
              />
            )}
          </Field>
          <Button type="submit" disabled={busy || token.trim().length < 10}>
            {busy ? "Anmeldung läuft…" : "Mit Token anmelden"}
          </Button>
        </form>
      </div>
    </details>
  );
}
